/**
 * chatWsBus — one WebSocket for the whole desktop app.
 *
 * The always-on notifications socket (useGlobalNotificationsWs, mounted in the
 * sidebar for the app's lifetime) is the ONLY chat socket. Every frame it
 * receives is republished here; pages that used to open a private second
 * socket (Chat, Calls) subscribe instead via useSharedChatWs.
 *
 * Why: the server delivers every frame to every socket a user holds, so the
 * old two-socket layout meant each message, presence flip, typing frame and
 * receipt was sent, parsed and dispatched twice, both sockets heartbeated,
 * and both acked delivery. One socket halves that work on both ends and
 * removes a whole class of "which socket is stale" reconnect races.
 *
 * Replay: a page can mount after the socket already opened. The bus remembers
 * whether the server has said `connected` and keeps the last
 * `presence_snapshot`, so a late subscriber can be handed both and run its
 * connect-time sync exactly as it did when it owned a socket.
 */

type Handler = (evt: any) => void;

const handlers = new Set<Handler>();
let sendImpl: ((data: object) => boolean) | null = null;
let connected = false;
let lastPresenceSnapshot: any | null = null;

/** Fan a received frame out to every subscriber (each isolated from the others). */
export function publishChatWsEvent(evt: any): void {
  const type = evt?.type;
  if (type === 'connected') connected = true;
  else if (type === 'ws_disconnected' || type === 'ws_unauthorized') connected = false;
  else if (type === 'presence_snapshot') lastPresenceSnapshot = evt;

  for (const handler of Array.from(handlers)) {
    try {
      handler(evt);
    } catch {
      /* one page's handler must not break delivery to the others */
    }
  }
}

/** Registered by the socket owner; `null` while there is no live socket. */
export function setChatWsSend(fn: ((data: object) => boolean) | null): void {
  sendImpl = fn;
}

/** Write a frame on the shared socket. False when nothing is connected. */
export function chatWsSend(data: object): boolean {
  if (!sendImpl) return false;
  try {
    return sendImpl(data);
  } catch {
    return false;
  }
}

export function subscribeChatWs(handler: Handler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

export function isChatWsConnected(): boolean {
  return connected;
}

export function getLastPresenceSnapshot(): any | null {
  return lastPresenceSnapshot;
}

export function chatWsSubscriberCount(): number {
  return handlers.size;
}

/** Test hook — forget every subscriber and all remembered state. */
export function __resetChatWsBusForTests(): void {
  handlers.clear();
  sendImpl = null;
  connected = false;
  lastPresenceSnapshot = null;
}
